import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { garagesApi, uploadUrl, type FeaturedGarageEntry } from '../../api/client';
import styles from './FeaturedGarages.module.css';

function EntryCard({ entry, rank, details }: { entry: FeaturedGarageEntry; rank: number; details: string[] }) {
  const avatarSrc = entry.user.avatarUrl
    ? entry.user.avatarUrl.startsWith('http')
      ? entry.user.avatarUrl
      : uploadUrl(entry.user.avatarUrl)
    : null;

  return (
    <li className={styles.row}>
      <span className={styles.rank}>{rank}</span>
      <Link to={`/garages/@${entry.user.username}`} className={styles.card}>
        <div className={styles.cardMain}>
          <div className={styles.preview}>
            {entry.previewPhotoUrl ? (
              <img src={entry.previewPhotoUrl} alt="" />
            ) : (
              <span className={styles.placeholder}>🚗</span>
            )}
          </div>
          <div className={styles.info}>
            <span className={styles.displayName}>{entry.user.displayName}</span>
            <div className={styles.counts}>
              {details.map((detail) => (
                <span key={detail} className={styles.detailLine}>
                  {detail}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.userAvatar} aria-hidden>
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className={styles.userAvatarImg} />
          ) : (
            <span className={styles.userAvatarLetter}>{entry.user.displayName.charAt(0) || '?'}</span>
          )}
        </div>
      </Link>
    </li>
  );
}

export function FeaturedGarages() {
  const [byEngagement, setByEngagement] = useState<FeaturedGarageEntry[]>([]);
  const [byPopularity, setByPopularity] = useState<FeaturedGarageEntry[]>([]);
  const [byCompleteness, setByCompleteness] = useState<FeaturedGarageEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await garagesApi.featured();
      setByEngagement(res.byEngagement);
      setByPopularity(res.byPopularity);
      setByCompleteness(res.byCompleteness);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load featured garages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={styles.page}>
      <h1>Featured Garages</h1>
      <p className={styles.subtitle}>
        Garages ranked by how much the community engages with owner's spots and cars, and by how complete and detailed their garage profiles are.
      </p>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          <section className={styles.section}>
            <h2>By community engagement</h2>
            <p className={styles.legend}>
              Ranked by total likes, beautiful, cool, sweet photo, and comments on their spots (posts) and on spots where their car was tagged.
            </p>
            {byEngagement.length === 0 ? (
              <p className={styles.empty}>No garages with tagged spots yet.</p>
            ) : (
              <ol className={styles.list}>
                {byEngagement.map((entry, index) => (
                  <EntryCard
                    key={entry.user.id}
                    entry={entry}
                    rank={index + 1}
                    details={[
                      `Engagement: ${entry.engagementScore ?? 0} reactions on ${entry.spotCount ?? 0} spot${(entry.spotCount ?? 0) === 1 ? '' : 's'}`,
                      `Spot Comments: ${entry.commentCount ?? 0}`,
                      `Car Tagged in Spots: ${entry.taggedSpotCount ?? 0}`,
                      `Cars in Garage: ${entry.garageCarCount}`,
                    ]}
                  />
                ))}
              </ol>
            )}
          </section>

          <section className={styles.section}>
            <h2>By popularity</h2>
            <p className={styles.legend}>
              Ranked by likes and comments on their garage cars.
            </p>
            {byPopularity.length === 0 ? (
              <p className={styles.empty}>No garages with popular garage activity yet.</p>
            ) : (
              <ol className={styles.list}>
                {byPopularity.map((entry, index) => (
                  <EntryCard
                    key={entry.user.id}
                    entry={entry}
                    rank={index + 1}
                    details={[
                      `Popularity: ${entry.popularityScore ?? 0} garage likes + comments`,
                      `Garage Comments: ${entry.commentCount ?? 0}`,
                      `Cars in Garage: ${entry.garageCarCount}`,
                    ]}
                  />
                ))}
              </ol>
            )}
          </section>

          <section className={styles.section}>
            <h2>By garage completeness</h2>
            <p className={styles.legend}>
              Ranked by how filled-out each garage car is: photos, mods, performance specs, build progress, engine details.
            </p>
            {byCompleteness.length === 0 ? (
              <p className={styles.empty}>No garages with detailed profiles yet.</p>
            ) : (
              <ol className={styles.list}>
                {byCompleteness.map((entry, index) => (
                  <EntryCard
                    key={entry.user.id}
                    entry={entry}
                    rank={index + 1}
                    details={[
                      `Completeness: ${entry.completenessScore ?? 0} pts`,
                      `Cars in Garage: ${entry.garageCarCount}`,
                    ]}
                  />
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </div>
  );
}
